import React, { useState, useRef, useEffect, useLayoutEffect, FC } from "react";
import useProjectStore from "common/store/ProjectStore";

interface IProps {
  minZoomPercent: number;
  maxZoomPercent: number;
  zoomPercentStep: number;
  draggableElementId: string
  children: React.ReactNode;
}

const ZoomInOut: FC<IProps> = ({ minZoomPercent, maxZoomPercent, zoomPercentStep, draggableElementId, children }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const { zoomPercent, setZoomPercent } = useProjectStore();
  const containerRef = useRef<HTMLDivElement>(null);

  const truncateToNearestStep = (num: number) => {
    return Math.trunc(num * zoomPercentStep) / zoomPercentStep;
  };

  // Zoom in function
  const handleZoomIn = () => {
    setZoomPercent(Math.min(maxZoomPercent, truncateToNearestStep(zoomPercent + zoomPercentStep)));
  };

  // Zoom out function
  const handleZoomOut = () => {
    setZoomPercent(Math.max(minZoomPercent, truncateToNearestStep(zoomPercent - zoomPercentStep)));
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup function to stop observing when the component unmounts
    const containerRefCurrent = containerRef.current;
    return () => {
      if (containerRefCurrent) {
        resizeObserver.unobserve(containerRefCurrent);
      }
    };
  }, []); // Empty dependency array means this effect runs once after the initial render

  useLayoutEffect(() => {
    // Determine the "translated" dimensions based on current scale
    const scale = zoomPercent / 100;
    const midX = containerSize.width / 2;
    const midY = containerSize.height / 2;
    const scaledMidX = midX * scale;
    const scaledMidY = midY * scale;
    const maxDeviationX = (scaledMidX - midX) / scale;
    const maxDeviationY = (scaledMidY - midY) / scale;

    if (
      position.x < -maxDeviationX ||
      position.x > maxDeviationX ||
      position.y < -maxDeviationY ||
      position.y > maxDeviationY
    ) {
      let newX = position.x;
      let newY = position.y;
      if (position.x < -maxDeviationX) {
        newX = -maxDeviationX;
      } else if (position.x > maxDeviationX) {
        newX = maxDeviationX;
      }

      if (position.y < -maxDeviationY) {
        newY = -maxDeviationY;
      } else if (position.y > maxDeviationY) {
        newY = maxDeviationY;
      }
      setPosition((position) => ({
        x: newX,
        y: newY,
      }));
    }
  }, [position, containerSize, zoomPercent]);

  // Effect for handling image dragging and zooming
  useEffect(() => {
    const container = containerRef.current;
    let isDragging = false;
    let prevPosition = { x: 0, y: 0 };

    // Mouse down event handler for starting image drag
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.id === draggableElementId) {
        isDragging = true;
        prevPosition = { x: e.clientX, y: e.clientY };
      }
    };

    // Mouse move event handler for dragging the image
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - prevPosition.x;
      const deltaY = e.clientY - prevPosition.y;
      prevPosition = { x: e.clientX, y: e.clientY };
      setPosition((position) => ({
        x: position.x + deltaX,
        y: position.y + deltaY,
      }));
    };

    // Mouse up event handler for ending image drag
    const handleMouseUp = () => {
      isDragging = false;
    };

    // Add event listeners
    container?.addEventListener("mousedown", handleMouseDown);
    container?.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Remove event listeners on component unmount
    return () => {
      container?.removeEventListener("mousedown", handleMouseDown);
      container?.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [containerRef, draggableElementId]); // Don't add "position" to list of dependencies or it'll mess up dragging

  const handleMouseWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (event.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  return (
    <div
      ref={containerRef}
      id='plot-plan-view-port'
      onWheel={handleMouseWheel}
      style={{
        backgroundColor: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "auto",
          height: "auto",
          transform: `scale(${zoomPercent / 100}) translate(${position.x}px, ${position.y}px)`,
          cursor: zoomPercent > 100 ? "move": "default"
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ZoomInOut;
